import { WorkItemResult } from "@buzzeasy/shared-frontend-utilities";
import { BaseQueryFn, FetchArgs, fetchBaseQuery, FetchBaseQueryError } from "@reduxjs/toolkit/query/react";
import buildQuery, { Filter } from "odata-query";
import environmentConfig from "../environmentConfig";
import { RootState } from "./store";

const { apiBaseUrl } = environmentConfig;

export function prepareHeaders(headers: Headers, { getState }: { getState(): unknown }): Headers {
  const { token } = (getState() as RootState).auth;

  if (!token)
    throw new Error("Empty 'token' found in state.");

  headers.set("authorization", `Bearer ${token}`);

  return headers;
}

export function apiBaseQuery(apiPath?: string): BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> {
  return async (args, store, extraOptions) => {
    const rawBaseQuery = fetchBaseQuery({
      baseUrl: new URL(apiPath ?? "", apiBaseUrl).href,
      prepareHeaders,
    });

    return rawBaseQuery(args, store, extraOptions);
  };
}

export const post = <T>(url: string, body: NonNullable<T>): FetchArgs => ({
  url,
  method: "POST",
  body,
});

export const put = <T>(url: string, body: NonNullable<T>): FetchArgs => ({
  url,
  method: "PUT",
  body,
});

// oData
const odataBaseUrls: Record<string, string> = {
  "https://api-dev.geomant.cloud": "https://odata-dev.buzzeasy.com",
  "https://api-qa.geomant.cloud": "https://odata-qa.buzzeasy.com",
  "https://api-prod.geomant.cloud": "https://odata.buzzeasy.com",
  "https://api-uk.geomant.cloud": "https://odata-uk.graia.ai",
};

export interface ODataListResult<T> {
  "@odata.count": number;
  "value": T[];
}

export function odataBaseQuery(): BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> {
  return async (args, store, extraOptions) => {
    const rawBaseQuery = fetchBaseQuery({
      baseUrl: new URL(odataBaseUrls[apiBaseUrl]).href,
      prepareHeaders,
    });

    return rawBaseQuery(args, store, extraOptions);
  };
}

export interface WorkItemFilters {
  searchTerm?: string;
  result?: WorkItemResult;
}

export function buildWorkItemQueryString(agentId: string, params: WorkItemFilters): string {
  const now = new Date();
  const from = new Date(now);
  from.setUTCDate(from.getUTCDate() - 7);
  from.setUTCHours(0, 0, 0, 0);
  // only voice tasks from the last 7 days for now
  const filters: Filter<unknown>[] = [
    { agentId },
    { mediaType: "voice" },
    { startTimeUtc: { ge: from } },
  ];

  if (params.searchTerm)
    filters.push({ conversations: { any: { customerPhoneNumber: { contains: params.searchTerm } } } });

  if (params.result)
    filters.push({ result: params.result });

  return buildQuery({
    count: true,
    orderBy: "startTimeUtc desc",
    expand: "conversations",
    filter: { and: filters },
  });
}
